import get from 'lodash.get';

import update from '../../../helpers/update';
import { getFormbuilderQuestions } from '../../../helpers/formBuilderHelper';
import {
  createDefaultComponentNode,
  createDefaultForm
} from '../../../components/formbuilder/formBuilderHelpers';
import { nodeTypes } from '../../../components/formbuilder/typeDefinitions';
import { formBuilderKeys } from '../../campaign/constants';

/** A donor address as the API returns it on `donor.address`. */
export interface DonorAddress {
  street?: string | null;
  city?: string | null;
  state?: string | null;
  postcode?: string | null;
  country?: string | null;
}

const addressContentFields: (keyof DonorAddress)[] = [
  'street',
  'city',
  'state',
  'postcode'
];

/**
 * Whether the donor has an address worth editing. The same rule the donation
 * view uses to show its address row: any of street, city, state or postcode.
 * A country on its own is not an address.
 */
export const hasDonorAddress = (
  address: DonorAddress | null | undefined
): boolean =>
  !!address &&
  addressContentFields.some((field) => {
    const value = address[field];
    return typeof value === 'string' && value.trim() !== '';
  });

/** A node of a form builder model: the root, a section or a question. */
export interface FormBuilderNode {
  type?: string;
  identifier?: string;
  props?: Record<string, unknown>;
  children?: FormBuilderNode[];
  [key: string]: unknown;
}

/** Whether a donation form builder model contains an address question. */
export const hasAddressQuestion = (questions: unknown): boolean =>
  getFormbuilderQuestions(questions).some(
    (question: { type?: string }) => question?.type === nodeTypes.address
  );

/**
 * The campaign's donation questions, plus an address question when the donor
 * has an address the campaign form does not ask for.
 *
 * The address editor on the donation edit screen is one of the form builder
 * questions, so without this a donation whose campaign never asked for an
 * address (or stopped asking) could not have its stored address edited. The
 * added question is never required, so it cannot block a save.
 */
export const withDonorAddressQuestion = (
  questions: FormBuilderNode | null | undefined,
  address: DonorAddress | null | undefined
): FormBuilderNode | null | undefined => {
  if (!hasDonorAddress(address) || hasAddressQuestion(questions)) {
    return questions;
  }

  const formKey = formBuilderKeys.CAMPAIGN_DONATIONS;
  // A campaign with no donation questions at all, or no section, gets an
  // empty form for the address to sit in.
  const form: FormBuilderNode = get(questions, 'children.0')
    ? (questions as FormBuilderNode)
    : createDefaultForm(formKey);

  return update.set(form, 'children.0.children', [
    ...getFormbuilderQuestions(form),
    createDefaultComponentNode(formKey, nodeTypes.address)
  ]);
};
